import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchData } from "@/utils/api-handler";

interface RoleState {
  list: RoleData[];
  scopes: Scope[];
}

interface RolesListResponse {
  items?: RoleData[];
}

/** Populates the org's role list in the store — dispatched by the session sync in user-data-refresh.tsx, mirroring fetchUser. */
export const fetchRoles = createAsyncThunk("roles/fetchRoles", async () => {
  const data = await fetchData("/api/rbac/roles?limit=100");
  return (
    Array.isArray(data) ? data : (data as RolesListResponse)?.items ?? []
  ) as RoleData[];
});

const loadStateFromLocalStorage = (): RoleState => {
  if (typeof window !== "undefined") {
    const savedRolesState = localStorage.getItem("rolesState");
    const savedScopesState = localStorage.getItem("scopesState");

    return {
      list: savedRolesState ? JSON.parse(savedRolesState) : [],
      scopes: savedScopesState ? JSON.parse(savedScopesState) : [],
    };
  }

  return { list: [], scopes: [] };
};

const initialState: RoleState = loadStateFromLocalStorage();

export const roleSlice = createSlice({
  name: "roles",
  initialState,
  reducers: {
    setRoles: (state, action: PayloadAction<RoleData[]>) => {
      state.list = action.payload;
      localStorage.setItem("rolesState", JSON.stringify(state.list));
    },
    setScopes: (state, action: PayloadAction<Scope[]>) => {
      state.scopes = action.payload;
      localStorage.setItem("scopesState", JSON.stringify(state.scopes));
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchRoles.fulfilled, (state, action) => {
      state.list = action.payload;
      localStorage.setItem("rolesState", JSON.stringify(state.list));
    });
  },
});

export const { setRoles, setScopes } = roleSlice.actions;
export default roleSlice.reducer;
